import { useCallback, useMemo, useState } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";

import { cn } from "../../utils/css";
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Badge,
} from "../shadcn";

import { ComboBoxListOption } from "./ComboBox";

export interface MultiSelectComboboxProps {
  list: ComboBoxListOption[];
  noFilterMatchesLabel?: string;
  placeholder?: string;
  value?: ComboBoxListOption[];
  onSelect: (selected: ComboBoxListOption) => void;
  onRemove: (removed: ComboBoxListOption) => void;
  suppressSelectedBadges?: boolean;
  onScroll?: (event: React.UIEvent<HTMLDivElement>) => void;
  isLoading?: boolean;
  onSearch?: (term: string) => void;
}

export const useMultiSelectCombobox = ({ initialSelectedItems = [] }: { initialSelectedItems?: ComboBoxListOption[] } = { initialSelectedItems: [] } ) => {
  const [selectedItems, setSelectedItems] =
    useState<ComboBoxListOption[]>(initialSelectedItems);

  const resetSelectedItems = useCallback(
    (newSelectedItems: ComboBoxListOption[] = []) => {
      setSelectedItems(newSelectedItems);
    },
    [setSelectedItems]
  );

  const handleSelect = useCallback(
    (item: ComboBoxListOption) => {
      setSelectedItems((prev) => {
        if (prev.some((i) => i.value === item.value)) {
          return prev.filter((i) => i.value !== item.value);
        } else {
          return [...prev, item];
        }
      });
    },
    [setSelectedItems]
  );

  const handleRemove = useCallback(
    (item: ComboBoxListOption) => {
      setSelectedItems((prev) => prev.filter((i) => i.value !== item.value));
    },
    [setSelectedItems]
  );

  return useMemo(
    () => ({
      resetSelectedItems,
      handleRemove,
      handleSelect,
      selectedItems,
      setSelectedItems,
    }),
    [
      resetSelectedItems,
      handleRemove,
      handleSelect,
      selectedItems,
      setSelectedItems,
    ]
  );
};

export default function MultiSelectCombobox({
  list,
  value = [],
  placeholder = "Select an option...",
  noFilterMatchesLabel = "No matches",
  onSelect,
  onRemove,
  suppressSelectedBadges = false,
  onScroll,
  isLoading = false,
  onSearch,
}: MultiSelectComboboxProps) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const filteredList = useMemo(() => {
    if (!onSearch && searchTerm) {
      return list.filter((item) =>
        item.label.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return list;
  }, [list, searchTerm, onSearch]);

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    onSearch?.(term);
  };

  const handleSelect = (item: ComboBoxListOption) => {
    onSelect(item);
    if (onSearch) {
      // Reset search term and trigger a new search with empty term
      setSearchTerm("");
      onSearch("");
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal={true}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between text-gray-primary hover:text-gray-primary"
        >
          {value.length > 0
            ? `${value.length} 
                            selected`
            : placeholder}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" style={{ zIndex: 9999 }} onOpenAutoFocus={(e) => e.preventDefault()}>
        <Command shouldFilter={false}>
          <CommandInput 
            placeholder={placeholder} 
            onValueChange={handleSearch}
            value={searchTerm}
          />
          <CommandEmpty>{noFilterMatchesLabel}</CommandEmpty>
          <div className="max-h-[300px] overflow-y-auto" onScroll={onScroll}>
            <CommandGroup>
              {filteredList.map((listOption) => (
                <CommandItem
                  key={listOption.value}
                  onSelect={() => handleSelect(listOption)}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value.some((item) => item.value === listOption.value)
                        ? "opacity-100"
                        : "opacity-0"
                    )}
                  />
                  {listOption.label}
                </CommandItem>
              ))}
              {isLoading && (
                <div className="flex items-center justify-center py-2">
                  <div className="w-4 h-4 border-2 border-solid border-t-blue-500 border-primary rounded-full animate-spin"></div>
                </div>
              )}
            </CommandGroup>
          </div>
        </Command>
      </PopoverContent>
      {!suppressSelectedBadges && !!value?.length && (
        <div className="mt-3">
          {value.map((item) => (
            <Badge key={item.value} variant="secondary" className="m-1">
              {item.label}
              <Button
                variant="ghost"
                size="sm"
                className="ml-2 h-4 w-4 p-0"
                onClick={() => onRemove(item)}
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          ))}
        </div>
      )}
    </Popover>
  );
}
